import { currencyFormatter } from '@/helpers/currencyFormatter';
import { useGetFinancingDataQuery } from '@/services/accounts';
import { Box, Button, Icon } from '@palmetto/palmetto-components';
import { DateTime } from 'luxon';
import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';

import { currentPaymentMethodLabels } from 'types';

import { useAuth } from '../auth/authProvider';
import { isPalmettoFinanceUserIncludingRoot } from '../auth/RequireIsInternalUser';

import { AccountNotesModal } from './AccountNotesModal';
import { AccountNotSentToBilling } from './AccountNotSentToBilling';
import { ActivePaymentLinkExistsModal } from './ActivePaymentLinkExistsModal';
import { CustomerPaymentHistory } from './CustomerPaymentHistory';
import { CustomerPaymentLinkHistory } from './CustomerPaymentLinkHistory';
import { ScheduledPaymentExistsModal } from './ScheduledPaymentExistsModal';
import { SendPaymentLinkModal } from './SendPaymentLinkModal';

const SUMMARY_ROW_DIRECTION = { base: 'column', tablet: 'row' } as const;
const PAYMENT_LINK_PLACEHOLDER_STYLE = { minWidth: '200px' };
const PAYMENT_LINK_WIDTH = { base: '100', tablet: '20' } as const;
const NOTES_BUTTON_HOVER = { color: 'body-primary' } as const;
const CARD_STYLE = { minWidth: 0 };
const AMOUNT_FONT_SIZE = { base: 'md', tablet: 'sm', desktop: 'lg', hd: '2xl' } as const;
const CARD_PADDING = { base: 'lg', tablet: 'xs', desktop: 'lg' } as const;

function formatCurrency(value: string | undefined): string {
  if (!value) return '$0.00';
  return currencyFormatter.format(parseFloat(value));
}

function formatDate(dateStr: string | undefined): string {
  if (!dateStr) return '';
  return DateTime.fromISO(dateStr, { zone: 'utc' }).toFormat('LLLL d, yyyy');
}

interface CustomerPaymentTabProps {
  account: any;
}

export function CustomerPaymentTab({ account }: CustomerPaymentTabProps) {
  const { id = '' } = useParams<{ id: string }>();
  const { claims } = useAuth();
  const hasFirstPaymentDueDate = Boolean(account?.firstPaymentDueDate);
  const {
    data: financingData,
    isLoading,
    isFetching,
  } = useGetFinancingDataQuery(id, {
    skip: !id || !isPalmettoFinanceUserIncludingRoot(claims) || !hasFirstPaymentDueDate,
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isScheduledPaymentModalOpen, setIsScheduledPaymentModalOpen] = useState(false);
  const [isActiveLinkModalOpen, setIsActiveLinkModalOpen] = useState(false);
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
  const openNotesModal = useCallback(() => setIsNotesModalOpen(true), []);
  const closeNotesModal = useCallback(() => setIsNotesModalOpen(false), []);
  const closeModal = useCallback(() => setIsModalOpen(false), []);
  const closeScheduledPaymentModal = useCallback(() => setIsScheduledPaymentModalOpen(false), []);
  const closeActiveLinkModal = useCallback(() => setIsActiveLinkModalOpen(false), []);
  const customerPaymentLinks = account?.inboundPayments?.customerPaymentLinks ?? [];
  const hasActivePaymentLink = customerPaymentLinks.some(
    (l: { expiresAt: string | Date; linkSuccessfullyUsedAt?: string | Date }) =>
      new Date(l.expiresAt) > new Date() && !l.linkSuccessfullyUsedAt,
  );
  const openModal = useCallback(() => {
    if (!financingData) return;
    if (financingData.hasScheduledOneTimePayment) {
      setIsScheduledPaymentModalOpen(true);
      return;
    }
    if (hasActivePaymentLink) {
      setIsActiveLinkModalOpen(true);
      return;
    }
    setIsModalOpen(true);
  }, [financingData, hasActivePaymentLink]);
  const scheduledPayment = financingData?.pendingPayments?.[0];

  const currentAmountDue = financingData?.normalMonthlyBillingCharges;
  const pastDueAmount = financingData?.amountPastDue;
  const totalDue = financingData?.totalDue;
  const lateChargeAmount = financingData?.lateChargeAmount;
  const nextPaymentDate = financingData?.nextScheduledAutoPaymentDate;
  const hasPaymentDue = nextPaymentDate && !nextPaymentDate.startsWith('0');
  const daysDelinquent = financingData?.daysDelinquent;
  const isPastDue = parseFloat(pastDueAmount ?? '0') > 0;
  const hasLateCharges = parseFloat(lateChargeAmount ?? '0') > 0;
  const currentPaymentMethodLabel =
    hasFirstPaymentDueDate &&
    financingData?.currentPaymentMethod &&
    currentPaymentMethodLabels[financingData.currentPaymentMethod]
      ? currentPaymentMethodLabels[financingData.currentPaymentMethod]
      : '---';
  const isOneTimePaymentBlocked = financingData?.isOneTimePaymentAllowed === false;

  if (!hasFirstPaymentDueDate) {
    return <AccountNotSentToBilling />;
  }

  return (
    <Box padding="lg" childGap="xl">
      {/* Summary cards row */}
      <Box direction={SUMMARY_ROW_DIRECTION} childGap="md" alignItems="stretch">
        {/* Monthly Billing Charges */}
        <Box
          borderWidth="xs"
          borderColor="separator"
          radius="md"
          padding={CARD_PADDING}
          childGap="sm"
          flex="auto"
          style={CARD_STYLE}
        >
          <Box as="p" fontSize="sm" fontWeight="medium">
            MONTHLY BILLING CHARGES
          </Box>
          <Box as="p" fontSize={AMOUNT_FONT_SIZE} fontWeight="bold">
            {isLoading ? '—' : formatCurrency(currentAmountDue)}
          </Box>
          <Box as="p" color="body-secondary" fontSize="sm" fontWeight="medium">
            {hasPaymentDue ? `Due ${formatDate(nextPaymentDate)}` : 'No payment due'}
          </Box>
          <Box as="p" color="body-secondary" fontSize="sm" fontWeight="medium">
            Payment plan: {currentPaymentMethodLabel}
          </Box>
          {isPalmettoFinanceUserIncludingRoot(claims) && (
            <Box
              as="button"
              type="button"
              alignSelf="flex-start"
              alignItems="center"
              direction="row"
              childGap="xs"
              padding="0"
              background="transparent"
              borderWidth="0"
              cursor="pointer"
              color="body-secondary"
              fontSize="sm"
              fontWeight="medium"
              hover={NOTES_BUTTON_HOVER}
              onClick={openNotesModal}
            >
              <Icon name="note" size="sm" />
              Account notes
            </Box>
          )}
        </Box>

        {/* Past due amount */}
        <Box
          borderWidth="xs"
          borderColor="separator"
          radius="md"
          padding={CARD_PADDING}
          childGap="sm"
          flex="auto"
          style={CARD_STYLE}
        >
          <Box as="p" fontSize="sm" fontWeight="medium">
            PAST DUE AMOUNT
          </Box>
          <Box as="p" fontSize={AMOUNT_FONT_SIZE} fontWeight="bold" color={isPastDue ? 'danger' : undefined}>
            {isLoading ? '—' : formatCurrency(pastDueAmount)}
          </Box>
          {!!daysDelinquent && (
            <Box as="p" color="body-secondary" fontSize="sm" fontWeight="medium">
              {daysDelinquent} {daysDelinquent === 1 ? 'day' : 'days'} past due
            </Box>
          )}
        </Box>

        {/* late charges */}
        <Box
          borderWidth="xs"
          borderColor="separator"
          radius="md"
          padding={CARD_PADDING}
          childGap="sm"
          flex="auto"
          style={CARD_STYLE}
        >
          <Box as="p" fontSize="sm" fontWeight="medium">
            LATE CHARGES
          </Box>
          <Box
            as="p"
            fontSize={AMOUNT_FONT_SIZE}
            fontWeight="bold"
            color={hasLateCharges ? 'warning-600' : undefined}
          >
            {isLoading ? '—' : formatCurrency(lateChargeAmount)}
          </Box>
        </Box>

        <Box
          borderWidth="xs"
          borderColor="separator"
          radius="md"
          padding={CARD_PADDING}
          childGap="sm"
          flex="auto"
          style={CARD_STYLE}
        >
          <Box as="p" fontSize="sm" fontWeight="medium">
            TOTAL AMOUNT DUE
          </Box>
          <Box as="p" fontSize={AMOUNT_FONT_SIZE} fontWeight="bold">
            {isLoading ? '—' : formatCurrency(totalDue)}
          </Box>
          <Box as="p" color="body-secondary" fontSize="sm" fontWeight="medium">
            Includes past due and late charges
          </Box>
        </Box>

        {/* Send Direct Payment Link */}
        <Box
          borderWidth="xs"
          borderColor="separator"
          radius="md"
          padding="lg"
          justifyContent="center"
          alignItems="center"
          flex="none"
          width={PAYMENT_LINK_WIDTH}
          style={PAYMENT_LINK_PLACEHOLDER_STYLE}
        >
          <Button
            type="button"
            variant="primary"
            iconPrefix="send"
            size="md"
            onClick={openModal}
            isDisabled={isLoading || isFetching || !financingData || isOneTimePaymentBlocked}
          >
            Send Direct Payment Link
          </Button>
          {isOneTimePaymentBlocked && (
            <Box as="p" color="danger" fontSize="sm" fontWeight="medium" margin="sm 0 0 0">
              Cannot create a payment link based on the account&apos;s status. Please reach out to Concord.
            </Box>
          )}
        </Box>
      </Box>

      <CustomerPaymentHistory />
      <CustomerPaymentLinkHistory links={account?.inboundPayments?.customerPaymentLinks ?? []} />

      <SendPaymentLinkModal
        isOpen={isModalOpen}
        onClose={closeModal}
        account={account}
        financingData={financingData}
      />

      {isPalmettoFinanceUserIncludingRoot(claims) && (
        <AccountNotesModal isOpen={isNotesModalOpen} onClose={closeNotesModal} accountId={id} />
      )}

      <ScheduledPaymentExistsModal
        isOpen={isScheduledPaymentModalOpen}
        onClose={closeScheduledPaymentModal}
        scheduledPayment={scheduledPayment}
        title="Send Direct Payment Link"
      />

      <ActivePaymentLinkExistsModal
        isOpen={isActiveLinkModalOpen}
        onClose={closeActiveLinkModal}
        links={customerPaymentLinks}
      />
    </Box>
  );
}
